import { BRAND } from "@/brand/brand";
import { useState, useCallback, useMemo, useEffect, useId } from "react";
import { Check, Copy, Loader2 } from "lucide-react";

import { createApiKey, CreateApiKeyResponse, userAPI } from "../UserAPI";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { ClaudeCodeIcon, ClaudeDesktopIcon, ChatGPTIcon, CursorIcon, VSCodeIcon } from "./clientIcons";
import ToolConfigurationForm from "../real-gain/ToolConfigurationForm";
import type { ConfigurationParameter } from "../real-gain/ConfigurationSchemaEditor";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

interface McpConnectModalProps {
  isOpen: boolean;
  product: { id: string; name: string; provider?: string; configurationSchema?: ConfigurationParameter[] };
  onClose: () => void;
  onApiKeyCreated?: () => void;
  canCreateApiKey?: boolean;
  bundled?: boolean;
}

/**
 * Radix-`Select` erlaubt keinen leeren Item-Wert; „Unbegrenzt“ läuft deshalb
 * über diesen Platzhalter und wird beim Setzen wieder auf "" abgebildet — der
 * Wert, den `handleCreateApiKey` als „kein Ablaufdatum“ interpretiert.
 */
const NEVER_EXPIRES = "never";

/** Nur-Lese-Feld mit Kopierknopf (MCP-Name, MCP-URL, API-Schlüssel). */
function CopyField({
  id,
  label,
  value,
  copied,
  copyLabel,
  onCopy,
  children,
}: {
  id: string;
  label: string;
  value: string;
  copied: boolean;
  copyLabel: string;
  onCopy: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id} className="font-semibold">{label}</Label>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Input
          id={id}
          value={value}
          readOnly
          className="min-w-0 flex-1 font-mono text-xs"
        />
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={copyLabel}
          title={copyLabel}
          onClick={onCopy}
        >
          {copied
            ? <Check aria-hidden className="h-4 w-4 text-success" />
            : <Copy aria-hidden className="h-4 w-4" />}
        </Button>
        {children}
      </div>
    </div>
  );
}

export default function McpConnectModal({
  isOpen,
  product,
  onClose,
  onApiKeyCreated,
  canCreateApiKey = true,
  bundled = false,
}: McpConnectModalProps) {
  const { t } = useTranslation();
  const fieldId = useId();

  const expirationOptions = [
    { key: "30", text: t("mcpModal.expires30Days") },
    { key: "90", text: t("mcpModal.expires90Days") },
    { key: "365", text: t("mcpModal.expires1Year") },
    { key: "", text: t("mcpModal.expiresNever") },
  ];

  // Auth mode toggle
  const [authMode, setAuthMode] = useState<"oauth" | "api">("oauth");

  // OAuth-specific state
  const [oauthNameCopied, setOauthNameCopied] = useState(false);
  const [oauthUrlCopied, setOauthUrlCopied] = useState(false);

  // API-specific state (existing)
  const [selectedClient, setSelectedClient] = useState<"claude-code" | "cursor" | "vscode">("claude-code");
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [commandCopied, setCommandCopied] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<string>("365");
  const [keySource, setKeySource] = useState<"created" | "manual" | null>(null);

  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  const [manualKeyDialogOpen, setManualKeyDialogOpen] = useState(false);
  const [manualKeyValue, setManualKeyValue] = useState("");
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"reset" | "close">("reset");

  // Tool configuration (tenant-scoped, for orders)
  const [authentication] = useAtom(authenticationAtom) as any;
  const [toolConfigValues, setToolConfigValues] = useState<Record<string, string>>({});
  const [savingToolConfig, setSavingToolConfig] = useState(false);
  const [fetchedSchema, setFetchedSchema] = useState<any[]>([]);

  const oauthNameId = `${fieldId}-oauth-name`;
  const oauthUrlId = `${fieldId}-oauth-url`;
  const apiUrlId = `${fieldId}-api-url`;
  const apiKeyId = `${fieldId}-api-key`;
  const installCommandId = `${fieldId}-install-command`;
  const keyNameId = `${fieldId}-key-name`;
  const expirationId = `${fieldId}-expiration`;
  const manualKeyId = `${fieldId}-manual-key`;

  const schemaFromProduct = product.configurationSchema ?? [];
  const schema = schemaFromProduct.length > 0 ? schemaFromProduct : fetchedSchema;
  const hasToolConfigSchema = !bundled && schema.length > 0;

  useEffect(() => {
    if (!isOpen || bundled || !product.id || !authentication?.customer) {
      setToolConfigValues({});
      setFetchedSchema([]);
      return;
    }
    const load = async () => {
      try {
        const config = await userAPI.getToolConfiguration(authentication.customer, product.id);
        setToolConfigValues(config?.schemaConfiguration ?? {});
      } catch {
        setToolConfigValues({});
      }
    };
    load();
  }, [isOpen, bundled, product.id, authentication?.customer]);

  // Fetch offer to get configurationSchema when not provided by order
  useEffect(() => {
    if (!isOpen || bundled || !product.id || schemaFromProduct.length > 0) {
      setFetchedSchema([]);
      return;
    }
    const fetchOffer = async () => {
      try {
        const offer = await userAPI.getMarketplaceOfferById(product.id);
        setFetchedSchema(offer?.configurationSchema ?? []);
      } catch {
        setFetchedSchema([]);
      }
    };
    fetchOffer();
  }, [isOpen, bundled, product.id, schemaFromProduct.length]);

  const handleSaveToolConfig = useCallback(async () => {
    if (!product.id || !authentication?.customer) return;
    setSavingToolConfig(true);
    try {
      const strValues: Record<string, string> = {};
      for (const [k, v] of Object.entries(toolConfigValues)) {
        strValues[k] = String(v ?? "");
      }
      await userAPI.putToolConfiguration(
        authentication.customer,
        product.id,
        strValues,
        schema,
      );
      toast.success(t("mcpModal.toolConfigSaved"));
    } finally {
      setSavingToolConfig(false);
    }
  }, [product.id, authentication?.customer, toolConfigValues, schema, t]);

  // MCP Name
  const mcpName = useMemo(() => {
    if (bundled) return `${BRAND.name} MCP`;
    if (product.provider) return `${product.provider} – ${product.name} MCP`;
    return `${product.name} MCP`;
  }, [bundled, product.provider, product.name]);

  // MCP URL (shared between both tabs)
  const mcpUrl = useMemo(() => {
    if (bundled) return "https://api.the-real-insight.ai/mcp";
    return `https://api.the-real-insight.ai/mcp/${product.id}`;
  }, [bundled, product.id]);

  // MCP Server Name (for API tab CLI/JSON)
  const getMcpServerName = useCallback(() => {
    if (bundled) return "the-real-insight-mcp";
    if (product.name) {
      const slug = product.name
        .toLowerCase()
        .replace(/[äöüß]/g, (c: string) =>
          ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c] || c)
        )
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      return `tri-${slug}`;
    }
    return `tri-${product.id}`;
  }, [bundled, product.name, product.id]);

  // API install commands
  const getInstallCommand = useCallback(() => {
    const apiKeyDisplay = generatedApiKey || "YOUR_API_KEY";
    const mcpServerName = getMcpServerName();

    if (selectedClient === "claude-code") {
      return `claude mcp add --transport http ${mcpServerName} ${mcpUrl} \\\n  --header "X-API-KEY: ${apiKeyDisplay}"`;
    } else if (selectedClient === "cursor") {
      return `// Add to .cursor/mcp.json\n{\n  "mcpServers": {\n    "${mcpServerName}": {\n      "url": "${mcpUrl}",\n      "headers": {\n        "X-API-KEY": "${apiKeyDisplay}"\n      }\n    }\n  }\n}`;
    } else if (selectedClient === "vscode") {
      return `// Add to settings.json\n"mcp.servers": {\n  "${mcpServerName}": {\n    "url": "${mcpUrl}",\n    "headers": {\n      "X-API-KEY": "${apiKeyDisplay}"\n    }\n  }\n}`;
    }
    return "";
  }, [selectedClient, generatedApiKey, mcpUrl, getMcpServerName]);

  const doClose = useCallback(() => {
    setGeneratedApiKey(null);
    setKeySource(null);
    setApiKeyCopied(false);
    setUrlCopied(false);
    setCommandCopied(false);
    setOauthNameCopied(false);
    setOauthUrlCopied(false);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (authMode === "api" && keySource === "created") {
      setConfirmAction("close");
      setConfirmResetOpen(true);
      return;
    }
    doClose();
  }, [authMode, keySource, doClose]);

  const copyToClipboard = useCallback(async (text: string, onSuccess: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess();
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  // OAuth copy handlers
  const handleCopyOAuthName = useCallback(() => {
    copyToClipboard(mcpName, () => {
      setOauthNameCopied(true);
      setTimeout(() => setOauthNameCopied(false), 2000);
    });
  }, [copyToClipboard, mcpName]);

  const handleCopyOAuthUrl = useCallback(() => {
    copyToClipboard(mcpUrl, () => {
      setOauthUrlCopied(true);
      setTimeout(() => setOauthUrlCopied(false), 2000);
    });
  }, [copyToClipboard, mcpUrl]);

  // API copy handlers
  const handleCopyUrl = useCallback(() => {
    copyToClipboard(mcpUrl, () => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  }, [copyToClipboard, mcpUrl]);

  const handleCopyApiKey = useCallback(() => {
    if (!generatedApiKey) return;
    copyToClipboard(generatedApiKey, () => {
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    });
  }, [copyToClipboard, generatedApiKey]);

  const handleCopyCommand = useCallback(() => {
    copyToClipboard(getInstallCommand(), () => {
      setCommandCopied(true);
      setTimeout(() => setCommandCopied(false), 2000);
    });
  }, [copyToClipboard, getInstallCommand]);

  const handleCreateApiKey = useCallback(async () => {
    if (!keyName.trim()) return;
    setCreatingApiKey(true);
    try {
      const result: CreateApiKeyResponse = await createApiKey(
        keyName.trim(),
        expiresInDays ? parseInt(expiresInDays) : undefined
      );
      if (result.accessToken) {
        setGeneratedApiKey(result.accessToken);
        setKeySource("created");
        setCreateKeyDialogOpen(false);
      }
      onApiKeyCreated?.();
    } catch (err) {
      console.error("Failed to create API key:", err);
    } finally {
      setCreatingApiKey(false);
    }
  }, [keyName, expiresInDays, onApiKeyCreated]);

  const handleOpenCreateKeyDialog = useCallback(() => {
    setKeyName("");
    setExpiresInDays("365");
    setCreateKeyDialogOpen(true);
  }, []);

  const handleCloseCreateKeyDialog = useCallback(() => {
    if (creatingApiKey) return;
    setCreateKeyDialogOpen(false);
  }, [creatingApiKey]);

  const handleOpenManualKeyDialog = useCallback(() => {
    setManualKeyValue("");
    setManualKeyDialogOpen(true);
  }, []);

  const handleCloseManualKeyDialog = useCallback(() => {
    setManualKeyDialogOpen(false);
    setManualKeyValue("");
  }, []);

  const handleSetManualKey = useCallback(() => {
    if (!manualKeyValue.trim()) return;
    setGeneratedApiKey(manualKeyValue.trim());
    setKeySource("manual");
    setManualKeyDialogOpen(false);
    setManualKeyValue("");
  }, [manualKeyValue]);

  const handleResetApiKey = useCallback(() => {
    if (keySource === "created") {
      setConfirmAction("reset");
      setConfirmResetOpen(true);
      return;
    }
    setGeneratedApiKey(null);
    setKeySource(null);
    setApiKeyCopied(false);
  }, [keySource]);

  const handleConfirmResetKey = useCallback(() => {
    setConfirmResetOpen(false);
    if (confirmAction === "close") {
      doClose();
    } else {
      setGeneratedApiKey(null);
      setKeySource(null);
      setApiKeyCopied(false);
    }
  }, [confirmAction, doClose]);

  const oauthSteps = [
    t("mcpModal.oauthStep2"),
    t("mcpModal.oauthStep3"),
    t("mcpModal.oauthStep4"),
  ];

  const copyLabel = (label: string) => t("mcpModal.copyValue", { label });

  return (
    <>
      {/* Main MCP Dialog */}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{t("mcpModal.title")}</DialogTitle>
            {bundled ? null : <DialogDescription>{product.name}</DialogDescription>}
          </DialogHeader>

          <div className="flex min-w-0 flex-col gap-4">
            {/* Tool Configuration (tenant credentials) */}
            {hasToolConfigSchema && (
              <div className="flex min-w-0 flex-col gap-3">
                <p className="text-sm font-semibold">{t("mcpModal.toolConfiguration")}</p>
                <ToolConfigurationForm
                  schema={schema}
                  value={toolConfigValues}
                  onChange={setToolConfigValues}
                />
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleSaveToolConfig} disabled={savingToolConfig}>
                    {savingToolConfig && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                    {savingToolConfig ? t("mcpModal.saving") : t("global.save")}
                  </Button>
                </div>
              </div>
            )}

            {/* Auth Mode Toggle */}
            <Tabs
              value={authMode}
              onValueChange={(value) => setAuthMode(value as "oauth" | "api")}
            >
              <TabsList className="overflow-x-auto">
                <TabsTrigger value="oauth" className="font-semibold">
                  {t("mcpModal.oauthTab")}
                </TabsTrigger>
                <TabsTrigger value="api" className="font-semibold">
                  {t("mcpModal.apiTab")}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="oauth"
                className="flex min-w-0 flex-col gap-4 duration-300 animate-in fade-in slide-in-from-bottom-3"
              >
                {/* OAuth: MCP Name */}
                <CopyField
                  id={oauthNameId}
                  label={t("mcpModal.mcpName")}
                  value={mcpName}
                  copied={oauthNameCopied}
                  copyLabel={copyLabel(t("mcpModal.mcpName"))}
                  onCopy={handleCopyOAuthName}
                />

                {/* OAuth: MCP URL */}
                <CopyField
                  id={oauthUrlId}
                  label={t("mcpModal.mcpUrl")}
                  value={mcpUrl}
                  copied={oauthUrlCopied}
                  copyLabel={copyLabel(t("mcpModal.mcpUrl"))}
                  onCopy={handleCopyOAuthUrl}
                />

                {/* OAuth Client Tabs — die Anleitung ist das Panel des gewählten Clients */}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p className="text-sm font-semibold">{t("mcpModal.clientLabel")}</p>
                  <Tabs value="claude-desktop">
                    <TabsList className="overflow-x-auto">
                      <TabsTrigger value="claude-desktop">
                        <ClaudeDesktopIcon />
                        Claude
                      </TabsTrigger>
                      <TabsTrigger value="chatgpt" disabled>
                        <ChatGPTIcon />
                        {`ChatGPT ${t("mcpModal.chatgptComingSoon")}`}
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent
                      value="claude-desktop"
                      className="mt-4 flex min-w-0 flex-col gap-1.5"
                    >
                      <p className="text-sm font-semibold">{t("mcpModal.instructions")}</p>
                      <ol className="flex min-w-0 flex-col">
                        {oauthSteps.map((step, index) => (
                          <li key={index} className="flex min-w-0 gap-3">
                            <div className="flex flex-col items-center self-stretch">
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border-2 border-primary text-xs font-semibold text-primary">
                                {index + 1}
                              </span>
                              {index < oauthSteps.length - 1 && (
                                <span aria-hidden className="w-0.5 flex-1 bg-border" />
                              )}
                            </div>
                            <span className="min-w-0 break-words pb-3 text-sm">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </TabsContent>
                    <TabsContent value="chatgpt" />
                  </Tabs>
                </div>
              </TabsContent>

              <TabsContent
                value="api"
                className="flex min-w-0 flex-col gap-4 duration-300 animate-in fade-in slide-in-from-bottom-3"
              >
                {/* API: MCP URL */}
                <CopyField
                  id={apiUrlId}
                  label={t("mcpModal.mcpUrl")}
                  value={mcpUrl}
                  copied={urlCopied}
                  copyLabel={copyLabel(t("mcpModal.mcpUrl"))}
                  onCopy={handleCopyUrl}
                />

                {/* API Key */}
                {generatedApiKey ? (
                  <div className="flex min-w-0 flex-col gap-2">
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <Label htmlFor={apiKeyId} className="font-semibold">
                        {t("mcpModal.apiKeyLabel")}
                      </Label>
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <Input
                          id={apiKeyId}
                          value={`${generatedApiKey.substring(0, 20)}...`}
                          readOnly
                          className="min-w-0 flex-1 font-mono text-xs"
                        />
                        {keySource === "created" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            aria-label={copyLabel(t("mcpModal.apiKeyLabel"))}
                            title={copyLabel(t("mcpModal.apiKeyLabel"))}
                            onClick={handleCopyApiKey}
                          >
                            {apiKeyCopied
                              ? <Check aria-hidden className="h-4 w-4 text-success" />
                              : <Copy aria-hidden className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button variant="outline" onClick={handleResetApiKey}>
                          {t("mcpModal.change")}
                        </Button>
                      </div>
                    </div>
                    {keySource === "created" && (
                      <Alert variant="warning">
                        <AlertDescription>{t("mcpModal.keyWarning")}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                ) : (
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <p className="text-sm font-semibold">{t("mcpModal.apiKeyLabel")}</p>
                    <div className="flex flex-wrap gap-2">
                      {canCreateApiKey && (
                        <Button onClick={handleOpenCreateKeyDialog}>
                          {t("mcpModal.createKey")}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        title={t("mcpModal.useExistingKeyTooltip")}
                        onClick={handleOpenManualKeyDialog}
                      >
                        {t("mcpModal.useExistingKey")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* API Client Tabs — der Installationsbefehl ist das Panel des gewählten Clients */}
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p className="text-sm font-semibold">{t("mcpModal.clientLabel")}</p>
                  <Tabs
                    value={selectedClient}
                    onValueChange={(value) =>
                      setSelectedClient(value as "claude-code" | "cursor" | "vscode")
                    }
                  >
                    <TabsList className="overflow-x-auto">
                      <TabsTrigger value="claude-code">
                        <ClaudeCodeIcon />
                        Claude Code
                      </TabsTrigger>
                      <TabsTrigger value="cursor">
                        <CursorIcon />
                        Cursor
                      </TabsTrigger>
                      <TabsTrigger value="vscode">
                        <VSCodeIcon />
                        VS Code
                      </TabsTrigger>
                    </TabsList>
                    {(["claude-code", "cursor", "vscode"] as const).map((client) => (
                      <TabsContent
                        key={client}
                        value={client}
                        className="mt-4 flex min-w-0 flex-col gap-1.5"
                      >
                        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                          <Label htmlFor={installCommandId} className="font-semibold">
                            {t("mcpModal.installCommand")}
                          </Label>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            aria-label={copyLabel(t("mcpModal.installCommand"))}
                            title={copyLabel(t("mcpModal.installCommand"))}
                            onClick={handleCopyCommand}
                          >
                            {commandCopied
                              ? <Check aria-hidden className="h-4 w-4 text-success" />
                              : <Copy aria-hidden className="h-4 w-4" />}
                          </Button>
                        </div>
                        <Textarea
                          id={installCommandId}
                          value={getInstallCommand()}
                          readOnly
                          rows={client === "claude-code" ? 3 : 10}
                          className="font-mono text-xs leading-relaxed"
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="gap-2 pt-4 sm:gap-0">
            <Button variant="outline" onClick={handleClose}>
              {t("mcpModal.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create API Key Sub-Dialog */}
      <Dialog
        open={createKeyDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseCreateKeyDialog();
          }
        }}
      >
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-[450px]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("mcpModal.createKeyTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={keyNameId}>
                {t("mcpModal.keyNameLabel")}
                <span aria-hidden className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id={keyNameId}
                required
                disabled={creatingApiKey}
                placeholder={t("mcpModal.keyNamePlaceholder")}
                value={keyName}
                onChange={(event) => setKeyName(event.target.value)}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={expirationId}>{t("mcpModal.expiration")}</Label>
              <Select
                value={expiresInDays === "" ? NEVER_EXPIRES : expiresInDays}
                disabled={creatingApiKey}
                onValueChange={(value) =>
                  setExpiresInDays(value === NEVER_EXPIRES ? "" : value)
                }
              >
                <SelectTrigger id={expirationId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {expirationOptions.map((option) => (
                    <SelectItem
                      key={option.key || NEVER_EXPIRES}
                      value={option.key || NEVER_EXPIRES}
                    >
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 sm:gap-0">
            <Button
              variant="outline"
              disabled={creatingApiKey}
              onClick={handleCloseCreateKeyDialog}
            >
              {t("mcpModal.cancel")}
            </Button>
            <Button
              disabled={!keyName.trim() || creatingApiKey}
              onClick={handleCreateApiKey}
            >
              {creatingApiKey && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
              {creatingApiKey ? t("mcpModal.creating") : t("mcpModal.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual API Key Sub-Dialog */}
      <Dialog
        open={manualKeyDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseManualKeyDialog();
          }
        }}
      >
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{t("mcpModal.manualKeyTitle")}</DialogTitle>
            <DialogDescription>{t("mcpModal.manualKeySubtext")}</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={manualKeyId}>{t("mcpModal.apiKeyLabel")}</Label>
            <Input
              id={manualKeyId}
              placeholder="sk-tri-..."
              className="font-mono text-xs"
              value={manualKeyValue}
              onChange={(event) => setManualKeyValue(event.target.value)}
            />
          </div>
          <DialogFooter className="gap-2 pt-4 sm:gap-0">
            <Button variant="outline" onClick={handleCloseManualKeyDialog}>
              {t("mcpModal.cancel")}
            </Button>
            <Button disabled={!manualKeyValue.trim()} onClick={handleSetManualKey}>
              {t("mcpModal.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Reset API Key Sub-Dialog */}
      <Dialog
        open={confirmResetOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmResetOpen(false);
          }
        }}
      >
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-[420px]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("mcpModal.confirmResetTitle")}</DialogTitle>
          </DialogHeader>
          <Alert variant="warning">
            <AlertDescription>{t("mcpModal.confirmResetWarning")}</AlertDescription>
          </Alert>
          <DialogFooter className="gap-2 pt-4 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmResetOpen(false)}>
              {t("mcpModal.notYet")}
            </Button>
            <Button
              className="bg-warning text-warning-foreground hover:bg-warning/90"
              onClick={handleConfirmResetKey}
            >
              {t("mcpModal.continue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
